import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT, STREET_SPAWNS, STREET_SPAWN_Y } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite, NATALKA_SIZE } from '../ui/NatalkaSprite.js';
import { reportLocation } from '../ui/locationTracker.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { realtime } from '../api/realtime.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { fitCharacter } from '../ui/outdoorScale.mjs';
import { ROOM_PERSPECTIVE } from './roomPerspective.mjs';
import { ySortDepth, LABEL_DEPTH } from '../ui/ySort.js';
import { virtualInput } from '../ui/virtualInput.js';
import { isInputLocked } from '../ui/inputLock.js';
import { showToast } from '../ui/Toast.js';
import { createCarSprite , fitCarHitArea } from '../ui/CarSprite.js';
import { showCarCardModal, showMechanicMenu } from '../ui/CarCardModal.js';
import { zl } from '../ui/carDetailsRows.mjs';
import { NEAR } from '../ui/carCatalog.mjs';
import { isNear } from '../ui/quizLogic.mjs';
import { DEALER_WORLD_WIDTH, FLOOR_Y, SPOTS, PODIUM_Y, SPAWN, inExitZone } from './carDealerLayout.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { iconText } from '../ui/iconText.js';

const HALF_H = NATALKA_SIZE.h / 2;
const ROOM = 'car_dealer';
const MECHANIC_ID = 'npc:mechanic';
const CAR_ACTION = 'Auto {car}';
const SERVICE_ACTION = 'Serwis {wrench}';
const CAMERA_TAU = 0.35;   // camera lag, same feel as the street (see OutsideScene)
const TIP_TOUCH_MS = 2000; // a tapped tip on a touchscreen hides itself after this

// The dealership showroom: one long baked room you scroll through (bg_car_dealer.webp,
// see scripts/compose_showroom.py), six cars on their podium bays and Pan Mechanik in the
// service corner by the door. Geometry lives in carDealerLayout.js; every price and spec
// on the cards comes from the server catalog.
export class CarDealerScene extends Phaser.Scene {
  constructor() { super(SCENE.CAR_DEALER); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    const W = DEALER_WORLD_WIDTH;
    this._spawn = { x: data?.spawnX ?? SPAWN.x, y: data?.spawnY ?? SPAWN.y };
    // Scene instances are reused — forget the previous visit's camera target so the
    // first _followWithLag() snaps instead of panning in from wherever we left off.
    this._camTarget = null;

    if (this.textures.exists('bg_car_dealer')) {
      this.add.image(W / 2, GAME_HEIGHT / 2, 'bg_car_dealer').setDisplaySize(W, GAME_HEIGHT);
    } else {
      this.add.rectangle(W / 2, GAME_HEIGHT / 2, W, GAME_HEIGHT, 0xf3ead8);
      iconText(this, W / 2, 60, 'Salon samochodowy (brak tła)', {
        fontFamily: 'system-ui', fontSize: '28px', color: '#333',
      }).setOrigin(0.5);
    }

    this._buildShowroom();

    this.perspective = ROOM_PERSPECTIVE.car_dealer;   // smaller and slower towards the far edge
    this.natalka = new NatalkaSprite(this, this._spawn.x, this._spawn.y);
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.walker = new WalkController(this, this.natalka, {
      floorPolyline: [[0, FLOOR_Y + HALF_H], [1, FLOOR_Y + HALF_H]],   // feet; a constant line
      onBroadcast: (p) => realtime.move(p),
    });
    fitCharacter(this);
    this.walker.boundsXMin = 60;
    this.walker.boundsXMax = W - 60;
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });
    this._followWithLag(this.natalka);

    new MoneyBar(this);
    attachFriendsButton(this);
    attachCoordsDebug(this);

    this._unsub = gameState.on(() => {
      this.natalka?.setOutfit(gameState.raw?.inventory?.equipped);
    });
    this.events.once('shutdown', () => this._unsub?.());

    // Pan Mechanik is a server-driven NPC (backend NPC_DEFS) — he arrives through the
    // room roster and stands still; we only grab him to measure proximity.
    this.mechanic = null;
    this._presence = attachRemotePresence(this, ROOM, {
      onNpc: (id, avatar) => { if (id === MECHANIC_ID) this.mechanic = avatar; },
    });
    this._roomChat = attachRoomChat(this, ROOM);

    // Clicking a car / the mechanic does the same as the action button.
    this.input.on('pointerup', (p) => {
      if (isInputLocked(this)) return;
      const s = this.mechanic?.sprite;
      if (s && Math.abs(p.worldX - s.x) < s.displayWidth / 2 && Math.abs(p.worldY - s.y) < s.displayHeight / 2) {
        this._talkToMechanic();
        return;
      }
      const spot = SPOTS.find((sp) => Math.abs(p.worldX - sp.x) < 150 && Math.abs(p.worldY - sp.y) < 100);
      if (spot) this._openCard(spot);
    });

    this._spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this._zoneGuardUntil = Date.now() + 600;   // don't bounce straight back out onto the street
    this._exitTriggered = false;
    this._busy = false;
    this._talking = false;   // holding the mechanic (npcInteract) — released on shutdown
    this._actionLabel = null;
    this._actionTarget = null;

    this.events.once('shutdown', () => {
      const open = virtualInput.getAction()?.label;
      if (open === CAR_ACTION || open === SERVICE_ACTION) virtualInput.clearAction();
      if (this._talking) realtime.npcRelease(MECHANIC_ID);
      this.mechanic = null;
    });
  }

  // Six cars on their bays. Front portraits, all wearing the showroom's demo plate rather
  // than a real one, standing with their tyres on the podium lip (`spot.y` is derived from
  // it per model — see carDealerLayout).
  //
  // No permanent price tags: six labels under six cars turned the showroom into a
  // spreadsheet. The name and the price are a HOVER tip instead — one shared label that
  // follows the mouse from car to car — and the „Auto 🚗" button still opens the full card.
  _buildShowroom() {
    this._cars = [];
    for (const spot of SPOTS) {
      const sprite = createCarSprite(this, spot.model, 'front', spot.x, spot.y, { plate: 'SALON' });
      // Every car stands on the SAME line, so they all get the same depth (the podium's) and
      // fall back to creation order: the cars are wider than their bays, and drawing them
      // left to right means each one laps the car to its left — never over its own plate,
      // which sits at its left edge. Sorting by `spot.y` instead would order them by how much
      // transparent padding their art happens to have.
      sprite.setDepth(ySortDepth(PODIUM_Y));
      fitCarHitArea(sprite);
      sprite.on('pointerover', (p) => this._showTip(spot, sprite, p));
      // A touchscreen has no hover, so the tap that opens the card flashes the tip too and
      // it times itself out; a mouse pointer leaving the car hides it straight away.
      sprite.on('pointerdown', (p) => { if (p?.wasTouch) this._showTip(spot, sprite, p); });
      sprite.on('pointerout', () => { if (!this._tipTimer) this._tip.setVisible(false); });
      this._cars.push({ spot, sprite });
    }

    this._tip = iconText(this, 0, 0, '', {
      fontFamily: 'system-ui', fontSize: '18px', color: '#ffffff', align: 'center',
      fontStyle: 'bold', backgroundColor: '#1c2530dd', padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 1).setDepth(LABEL_DEPTH).setVisible(false);
    this._tipTimer = null;
  }

  _showTip(spot, sprite, pointer) {
    // The catalog only lands once the first /state arrives, which can be after create() —
    // reading it here (rather than baking the text at build time) means the tip is never stale.
    const spec = gameState.raw?.catalog?.cars?.[spot.model];
    this._tip.setText(spec ? `${spec.name_pl}\n${zl(spec.price)}` : spot.model);
    this._tip.setPosition(spot.x, spot.y - (sprite.img?.displayHeight ?? 200) / 2 - 8)
      .setVisible(true);
    this._tipTimer?.remove();
    this._tipTimer = null;
    if (pointer?.wasTouch) {
      this._tipTimer = this.time.delayedCall(TIP_TOUCH_MS, () => {
        this._tipTimer = null;
        this._tip.setVisible(false);
      });
    }
  }

  _followWithLag(target) {
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.roundPixels = false;   // sub-pixel scroll: whole-pixel steps read as stutter
    const snap = !this._camTarget;
    this._camTarget = target;
    if (snap) cam.scrollX = this._desiredScrollX();
  }

  _desiredScrollX() {
    const x = (this._camTarget?.x ?? 0) - GAME_WIDTH / 2;
    return Math.max(0, Math.min(DEALER_WORLD_WIDTH - GAME_WIDTH, x));
  }

  _updateCamera(dt) {
    if (!this._camTarget) return;
    const cam = this.cameras.main;
    const k = 1 - Math.exp(-(dt / 1000) / CAMERA_TAU);   // frame-rate independent easing
    cam.scrollX += (this._desiredScrollX() - cam.scrollX) * k;
  }

  async _openCard(spot) {
    if (this._busy || isInputLocked(this)) return;
    // Clicking works like walking up: you still have to be standing at the car.
    if (Math.abs(this.natalka.x - spot.x) > NEAR) { showToast(this, 'Podejdź bliżej do auta {car}'); return; }
    this._busy = true;
    try {
      await showCarCardModal(this, spot.model);
    } finally {
      this._busy = false;
    }
  }

  // Hold the NPC while the menu is open (he stops moving for everyone), release after.
  async _talkToMechanic() {
    if (this._busy || isInputLocked(this) || !this.mechanic) return;
    const s = this.mechanic.sprite;
    if (!isNear(this.natalka.x, this.natalka.y, s.x, s.y, NEAR)) {
      showToast(this, 'Podejdź do Pana Mechanika {wrench}');
      return;
    }
    this._busy = true;
    this._talking = true;
    realtime.npcInteract(MECHANIC_ID);
    try {
      await showMechanicMenu(this);
    } finally {
      realtime.npcRelease(MECHANIC_ID);
      this._talking = false;
      this._busy = false;
    }
  }

  // Whichever is closest wins the one action button: the mechanic if you are standing at
  // his counter, otherwise the car you are standing in front of.
  _syncAction() {
    let label = null, fn = null, target = null;
    const s = this.mechanic?.sprite;
    if (s && isNear(this.natalka.x, this.natalka.y, s.x, s.y, NEAR)) {
      label = SERVICE_ACTION;
      target = MECHANIC_ID;
      fn = () => this._talkToMechanic();
    } else {
      const spot = SPOTS.find((sp) => Math.abs(this.natalka.x - sp.x) <= NEAR);
      if (spot) { label = CAR_ACTION; target = spot.model; fn = () => this._openCard(spot); }
    }
    if (isInputLocked(this)) { label = null; target = null; }

    // Re-register on the TARGET, not just the label: walking from one car to the next
    // keeps the label „Auto 🚗” but must swap which car the button opens.
    const open = virtualInput.getAction()?.label;
    if (label && (open !== label || this._actionTarget !== target)) virtualInput.registerAction(label, fn);
    else if (!label && (open === CAR_ACTION || open === SERVICE_ACTION)) virtualInput.clearAction();
    this._actionLabel = label;
    this._actionTarget = target;
  }

  update(_t, dt) {
    this.walker.update(dt);
    fitCharacter(this);
    this._updateCamera(dt);
    reportLocation(this, this.natalka.x, this.natalka.y);
    this._depthSort();
    this._syncAction();

    if (Phaser.Input.Keyboard.JustDown(this._spaceKey) && !isInputLocked(this) && this._actionLabel) {
      virtualInput.invokeAction();
    }

    if (this._exitTriggered || Date.now() < this._zoneGuardUntil) return;
    if (inExitZone(this.natalka.x)) {
      this._exitTriggered = true;
      this.scene.start(SCENE.OUTSIDE, { spawnX: STREET_SPAWNS.CAR_DEALER, spawnY: STREET_SPAWN_Y, upExit: this.walker.walkingUp });
    }
  }

  _depthSort() {
    this.natalka.setDepth(ySortDepth(this.natalka.feetY));
    if (this._presence) for (const a of this._presence.avatars.values()) a.sprite.setDepth(ySortDepth((a.sprite.feetY ?? a.sprite.y + HALF_H)));
  }
}
